// Timed text feeds: the news ticker (headlines from sim events, sponsor ads in between; GDD §13) and toasts
// (rejections and targeting hints). Both run on timers bound to the HUD lifetime, never on the frame loop.

import { SPONSOR_SLOGANS } from './catalog'
import { el, Timers } from './dom'
import { HudError } from './errors'
import { DESK_LABELS, type Headline } from './headlines'

/** How long one ticker item stays up. */
const TICKER_DWELL_MS = 4500
/** Headlines waiting beyond this are dropped oldest-first: the ticker shows current news, not a backlog. */
const TICKER_QUEUE_CAP = 6
const SPONSOR_LABEL = 'SPONSORED'
const TICKER_BRAND = 'LIVE'

type TickerItemKind = 'headline' | 'ad'

export class HeadlineTicker {
  readonly element: HTMLElement
  private readonly line: HTMLElement
  private readonly queue: Headline[] = []
  private readonly timers: Timers
  private showing: TickerItemKind = 'ad'
  private nextAd = 0
  private pending = 0

  constructor(signal: AbortSignal) {
    this.element = el('section', 'tw-ticker tw-panel')
    this.element.setAttribute('aria-label', 'News ticker')
    this.line = el('div', 'tw-ticker__line')
    this.element.append(el('span', 'tw-ticker__brand', TICKER_BRAND), this.line)
    this.timers = new Timers(signal)
    this.rotate()
  }

  push(headline: Headline): void {
    if (this.queue.length >= TICKER_QUEUE_CAP) this.queue.shift()
    this.queue.push(headline)
    // News pre-empts an ad at once; a headline on screen keeps its dwell time.
    if (this.showing === 'ad') {
      this.timers.cancel(this.pending)
      this.rotate()
    }
  }

  private readonly rotate = (): void => {
    const headline = this.queue.shift()
    if (headline === undefined) this.show('ad', SPONSOR_LABEL, this.takeAd())
    else this.show('headline', DESK_LABELS[headline.desk], headline.text)
    this.pending = this.timers.after(TICKER_DWELL_MS, this.rotate)
  }

  private takeAd(): string {
    const slogan = SPONSOR_SLOGANS[this.nextAd % SPONSOR_SLOGANS.length]
    if (slogan === undefined) throw new HudError('no sponsor slogans to run')
    this.nextAd++
    return slogan
  }

  private show(kind: TickerItemKind, desk: string, text: string): void {
    this.showing = kind
    // A fresh element per item replays the entrance animation without forcing a layout.
    const entry = el('p', `tw-ticker__item tw-ticker__item--${kind}`)
    entry.append(el('span', 'tw-ticker__desk', desk), el('span', 'tw-ticker__text', text))
    this.line.replaceChildren(entry)
  }
}

/** Toasts visible at once; a new one pushes out the oldest. */
const TOAST_LIMIT = 3
const TOAST_MS = 3500

export type ToastTone = 'reject' | 'hint'

export class Toasts {
  readonly element: HTMLElement
  private readonly timers: Timers

  constructor(signal: AbortSignal) {
    this.element = el('div', 'tw-toasts')
    this.element.setAttribute('role', 'status')
    this.element.setAttribute('aria-live', 'polite')
    this.timers = new Timers(signal)
  }

  show(text: string, tone: ToastTone): void {
    while (this.element.childElementCount >= TOAST_LIMIT) this.element.firstElementChild?.remove()
    const toast = el('div', `tw-toast tw-panel tw-toast--${tone}`, text)
    this.element.append(toast)
    this.timers.after(TOAST_MS, () => toast.remove())
  }
}
